import { X, ExternalLink, Zap } from "lucide-react";
import { fmtMoney, fmtPct, displayTicker } from "../lib/format";

const TYPE_LABEL = {
  analyst_target: "12-mo analyst target",
  fair_value: "Intrinsic fair value",
  long_term_target: "Long-term price target",
};

const TYPE_STYLE = {
  analyst_target: "bg-moss-bg/60 border-moss-light/40 text-moss-light",
  fair_value: "bg-[#6366F1]/10 border-[#6366F1]/40 text-[#4347B5]",
  long_term_target: "bg-amber_brand-bg/70 border-amber_brand/40 text-amber_brand",
};

const CONVICTION_STYLE = {
  HIGH_UNDERVALUED: { emoji: "🟢", bg: "bg-moss text-bone-50 border-moss",            label: "HIGH-CONVICTION UNDERVALUED" },
  HIGH_OVERVALUED:  { emoji: "🔴", bg: "bg-terracotta text-bone-50 border-terracotta", label: "CONSENSUS OVERVALUED" },
  MIXED_SIGNAL:     { emoji: "🟡", bg: "bg-amber_brand-bg text-amber_brand border-amber_brand/50", label: "MIXED SIGNAL" },
  MIXED_BULLISH:    { emoji: "🟢", bg: "bg-moss-bg text-moss-light border-moss-light/50", label: "MIXED BULLISH" },
  MIXED_BEARISH:    { emoji: "🟠", bg: "bg-terracotta-bg text-terracotta border-terracotta/40", label: "MIXED BEARISH" },
  LOW_COVERAGE:     { emoji: "⚪", bg: "bg-bone-100 text-stone_brand border-line",   label: "LOW COVERAGE" },
  NEUTRAL:          { emoji: "⚪", bg: "bg-bone-100 text-stone_brand border-line",   label: "NEUTRAL" },
};

function ConvictionBanner({ conviction }) {
  if (!conviction) return null;
  const s = CONVICTION_STYLE[conviction.level] || CONVICTION_STYLE.NEUTRAL;
  return (
    <div
      className={`border rounded-sm px-4 py-3 ${s.bg}`}
      data-testid={`modal-conviction-${conviction.level}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="font-mono text-xs uppercase tracking-widest font-semibold whitespace-nowrap">
          {s.emoji} {s.label}
        </div>
        {conviction.spread_pct !== undefined && (
          <div className="font-mono text-[0.65rem] opacity-90 tracking-wider">
            {conviction.source_count} sources · spread {conviction.spread_pct}% · avg{" "}
            {conviction.avg_diff_pct >= 0 ? "+" : ""}
            {conviction.avg_diff_pct}%
          </div>
        )}
      </div>
      {conviction.detail && (
        <div className="font-mono text-[0.65rem] opacity-80 mt-1 leading-relaxed">
          {conviction.detail}
        </div>
      )}
    </div>
  );
}

export default function FairValueSourcesModal({ stock, onClose }) {
  if (!stock) return null;
  const sources = stock.fair_value_sources || [];
  const cur = stock.currency || "USD";
  const price = stock.current_price;

  // Diff from each source
  const withDiff = sources
    .map((s) => ({
      ...s,
      diff_pct: price && s.value ? ((s.value - price) / price) * 100 : null,
    }))
    .sort((a, b) => (b.diff_pct || -999) - (a.diff_pct || -999));

  return (
    <div
      className="fixed inset-0 z-[100] bg-stone_brand-dark/70 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      onClick={onClose}
      data-testid="sources-modal"
    >
      <div
        className="bg-bone-50 border border-line rounded-sm w-full max-w-2xl my-4 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-line px-6 py-4 flex items-center justify-between rounded-t-sm z-10">
          <div>
            <div className="overline text-stone_brand-light mb-0.5">— Fair Value Sources</div>
            <div className="font-serif-display text-xl text-stone_brand-dark font-medium leading-tight">
              {stock.company_name || displayTicker(stock.ticker)} · {displayTicker(stock.ticker)}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-stone_brand hover:text-stone_brand-dark hover:bg-bone-100 rounded-sm transition-colors"
            data-testid="sources-close-btn"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Conviction summary */}
          <ConvictionBanner conviction={stock.conviction} />

          {/* Current price reference */}
          <div className="flex items-center justify-between bg-white border border-line rounded-sm p-4">
            <div>
              <div className="overline mb-1">Current Price</div>
              <div className="font-mono text-2xl text-stone_brand-dark">
                {fmtMoney(price, cur)}
              </div>
            </div>
            <div className="text-right">
              <div className="overline mb-1">Consolidated FV (median)</div>
              <div className="font-mono text-2xl text-moss">
                {fmtMoney(stock.consolidated_fair_value, cur)}
              </div>
              <div className="text-xs font-mono text-stone_brand-light mt-0.5">
                Diff: {fmtPct(stock.consolidated_diff_percent)}
              </div>
            </div>
          </div>

          {/* Each source as a row */}
          <div>
            <div className="overline mb-3">— Who said what</div>
            {withDiff.length === 0 ? (
              <p className="text-sm text-stone_brand italic">No fair-value sources available.</p>
            ) : (
              <div className="space-y-2.5">
                {withDiff.map((s, i) => (
                  <div
                    key={i}
                    className={`border rounded-sm p-4 flex items-start justify-between gap-4 ${TYPE_STYLE[s.type] || TYPE_STYLE.analyst_target}`}
                    data-testid={`source-${s.source}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm font-semibold">{s.label}</span>
                        <span className="text-[0.6rem] font-mono uppercase tracking-widest opacity-70 border border-current/30 px-1.5 py-0.5 rounded-sm">
                          {TYPE_LABEL[s.type] || s.type}
                        </span>
                        {s.url && (
                          <a
                            href={s.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="opacity-60 hover:opacity-100 transition-opacity"
                            aria-label="Open source"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                      {s.detail && (
                        <div className="text-xs opacity-80 leading-relaxed">{s.detail}</div>
                      )}
                      {s.as_of && (
                        <div className="text-[0.65rem] font-mono opacity-60 mt-1">
                          AS OF {new Date(s.as_of).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-mono text-xl font-semibold">
                        {fmtMoney(s.value, cur)}
                      </div>
                      <div className="font-mono text-xs mt-0.5 opacity-90">
                        {s.diff_pct !== null ? fmtPct(s.diff_pct) : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-start gap-2 text-[0.7rem] font-mono tracking-wider text-stone_brand-light uppercase pt-3 border-t border-line">
            <Zap className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>
              Data refreshed hourly · Consolidated = median of analyst &amp; fair-value sources (long-term
              targets excluded). For 9-platform cross-check (Screener, Trendlyne, Moneycontrol, etc.),
              open the stock detail page.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
